/**
 * Shared components for displaying CV information.
 * Used by both CVDetailPage (authenticated) and PublicCVDetailPage (public).
 */

import {
  FileText,
  Mail,
  Phone,
  MapPin,
  Globe,
  Briefcase,
  GraduationCap,
  Award,
  Languages,
  Wrench,
  Calendar,
  Building2,
  X,
} from 'lucide-react';
import type { JsonResume, JsonResumeWork, JsonResumeEducation, JsonResumeSkill } from '../../types';
import { formatDateRange } from './utils';
import { useTranslation } from 'react-i18next';

// ==================== Sub Components ====================

/**
 * HTML Content component - safely renders HTML or plain text content.
 */
export function HtmlContent({ html, className }: { html: string; className?: string }) {
  const hasHtml = /<[^>]+>/.test(html);

  if (hasHtml) {
    return <div className={className} dangerouslySetInnerHTML={{ __html: html }} />;
  }

  return <p className={`${className} whitespace-pre-line`}>{html}</p>;
}

/**
 * Section Card wrapper component.
 */
export function SectionCard({
  title,
  icon: Icon,
  children,
}: {
  title: string;
  icon: React.ElementType;
  children: React.ReactNode;
}) {
  return (
    <div className="bg-white rounded-lg border border-gray-200 p-5">
      <div className="flex items-center gap-2 mb-4">
        <Icon className="h-5 w-5 text-gray-500" />
        <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
      </div>
      {children}
    </div>
  );
}

/**
 * Chip/Tag component for displaying labels.
 */
export function Chip({ label, onRemove }: { label: string; onRemove?: () => void }) {
  const { t } = useTranslation('ats');
  return (
    <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded text-sm font-medium bg-gray-100 text-gray-700">
      {label}
      {onRemove && (
        <button
          onClick={onRemove}
          className="ml-0.5 rounded-full hover:bg-gray-300 p-0.5 transition-colors"
          aria-label={t('cvInfo.removeSelectedAria', { name: label })}
        >
          <X className="h-3 w-3" />
        </button>
      )}
    </span>
  );
}

/**
 * Work Experience Item component.
 */
export function WorkItem({ work }: { work: JsonResumeWork }) {
  // Support both 'company' (backend) and 'name' (JSON Resume standard) fields
  const companyName = work.company || work.name;
  const location = work.location;
  const description = work.description;

  return (
    <div className="relative pl-6 pb-6 last:pb-0">
      {/* Timeline dot */}
      <div className="absolute left-0 top-1.5 w-3 h-3 bg-blue-500 rounded-full border-2 border-white shadow-sm" />

      {/* Timeline line */}
      <div className="absolute left-[5px] top-6 bottom-0 w-0.5 bg-gray-200 last:hidden" />

      {/* Content card */}
      <div className="bg-gray-50 rounded-lg p-4 hover:bg-gray-100 transition-colors">
        {/* Header with position and date */}
        <div className="flex justify-between items-start gap-4 mb-2">
          <div className="flex-1 min-w-0">
            <h4 className="text-base font-semibold text-gray-900 mb-1">
              {work.position}
            </h4>

            {/* Company info */}
            {companyName && (
              <div className="flex items-center gap-2 text-gray-700 mb-1">
                <Building2 className="h-4 w-4 text-gray-400 shrink-0" />
                <span className="font-medium">{companyName}</span>
              </div>
            )}

            {/* Meta info row */}
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-gray-600">
              {description && (
                <span className="flex items-center gap-1">
                  <Briefcase className="h-3.5 w-3.5 text-gray-400" />
                  {description}
                </span>
              )}
              {location && (
                <span className="flex items-center gap-1">
                  <MapPin className="h-3.5 w-3.5 text-gray-400" />
                  {location}
                </span>
              )}
            </div>
          </div>

          {/* Date badge */}
          {(work.startDate || work.endDate) && (
            <div className="flex items-center gap-1.5 px-3 py-1.5 bg-white rounded-md border border-gray-200 shrink-0">
              <Calendar className="h-3.5 w-3.5 text-gray-400" />
              <span className="text-xs font-medium text-gray-600 whitespace-nowrap">
                {formatDateRange(work.startDate, work.endDate)}
              </span>
            </div>
          )}
        </div>

        {/* Summary */}
        {work.summary && (
          <HtmlContent
            html={work.summary}
            className="mt-3 text-sm text-gray-700 leading-relaxed [&_ul]:list-disc [&_ul]:pl-5 [&_ul]:space-y-1 [&_li]:text-gray-700"
          />
        )}

        {/* Highlights */}
        {work.highlights && work.highlights.length > 0 && (
          <div className="mt-3 space-y-2">
            {work.highlights.map((highlight, idx) => (
              <div key={idx} className="flex items-start gap-2 text-sm text-gray-700">
                <div className="mt-1.5 w-1.5 h-1.5 bg-blue-500 rounded-full shrink-0" />
                <span className="leading-relaxed">{highlight}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * Education Item component.
 */
export function EducationItem({ edu }: { edu: JsonResumeEducation }) {
  return (
    <div className="relative pl-6 pb-6 last:pb-0">
      {/* Timeline dot */}
      <div className="absolute left-0 top-1.5 w-3 h-3 bg-green-500 rounded-full border-2 border-white shadow-sm" />

      {/* Timeline line */}
      <div className="absolute left-[5px] top-6 bottom-0 w-0.5 bg-gray-200 last:hidden" />

      {/* Content card */}
      <div className="bg-gray-50 rounded-lg p-4 hover:bg-gray-100 transition-colors">
        <div className="flex justify-between items-start gap-4">
          <div className="flex-1 min-w-0">
            <h4 className="text-base font-semibold text-gray-900 mb-1">
              {edu.institution}
            </h4>
            {edu.area && (
              <p className="text-sm text-gray-700 mb-1">
                {edu.studyType && <span className="font-medium">{edu.studyType}</span>}
                {edu.studyType && edu.area && <span className="text-gray-500"> in </span>}
                {edu.area && <span>{edu.area}</span>}
              </p>
            )}
            {edu.score && (
              <p className="text-sm text-gray-600">
                <span className="font-medium">GPA:</span> {edu.score}
              </p>
            )}
          </div>

          {/* Date badge */}
          {(edu.startDate || edu.endDate) && (
            <div className="flex items-center gap-1.5 px-3 py-1.5 bg-white rounded-md border border-gray-200 shrink-0">
              <Calendar className="h-3.5 w-3.5 text-gray-400" />
              <span className="text-xs font-medium text-gray-600 whitespace-nowrap">
                {formatDateRange(edu.startDate, edu.endDate)}
              </span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * Skill Item component.
 */
export function SkillItem({ skill }: { skill: JsonResumeSkill }) {
  return (
    <div className="bg-gray-50 rounded-lg p-4 hover:bg-gray-100 transition-colors">
      <div className="flex items-center gap-2 mb-2">
        <span className="text-base font-semibold text-gray-900">{skill.name}</span>
        {skill.level && (
          <span className="text-xs px-2.5 py-1 bg-blue-100 text-blue-700 rounded-full font-medium">
            {skill.level}
          </span>
        )}
      </div>
      {skill.keywords && skill.keywords.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {skill.keywords.map((keyword, idx) => (
            <span
              key={idx}
              className="text-xs px-2.5 py-1 bg-white text-gray-700 rounded-md border border-gray-200 font-medium"
            >
              {keyword}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

// ==================== Main Component ====================

/**
 * CV Info Display - Shows all parsed CV data sections.
 * Used by both authenticated and public CV detail pages.
 */
export function CVInfoDisplay({ data }: { data: JsonResume }) {
  const { t } = useTranslation('ats');
  const basics = data.basics;

  return (
    <div className="space-y-6">
      {/* Contact Info */}
      {basics && (basics.email || basics.phone || basics.location || basics.url) && (
        <SectionCard title={t('cvInfo.contactInformation')} icon={Mail}>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {basics.email && (
              <div className="flex items-center gap-3 bg-gray-50 rounded-lg p-3 hover:bg-gray-100 transition-colors">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <Mail className="h-4 w-4 text-blue-600" />
                </div>
                <a
                  href={`mailto:${basics.email}`}
                  className="text-sm text-blue-600 hover:underline font-medium truncate"
                >
                  {basics.email}
                </a>
              </div>
            )}
            {basics.phone && (
              <div className="flex items-center gap-3 bg-gray-50 rounded-lg p-3 hover:bg-gray-100 transition-colors">
                <div className="p-2 bg-green-100 rounded-lg">
                  <Phone className="h-4 w-4 text-green-600" />
                </div>
                <a
                  href={`tel:${basics.phone}`}
                  className="text-sm text-blue-600 hover:underline font-medium"
                >
                  {basics.phone}
                </a>
              </div>
            )}
            {basics.location?.address && (
              <div className="flex items-center gap-3 bg-gray-50 rounded-lg p-3">
                <div className="p-2 bg-orange-100 rounded-lg">
                  <MapPin className="h-4 w-4 text-orange-600" />
                </div>
                <span className="text-sm text-gray-700 font-medium truncate">
                  {basics.location.address}
                </span>
              </div>
            )}
            {basics.url && (
              <div className="flex items-center gap-3 bg-gray-50 rounded-lg p-3 hover:bg-gray-100 transition-colors">
                <div className="p-2 bg-purple-100 rounded-lg">
                  <Globe className="h-4 w-4 text-purple-600" />
                </div>
                <a
                  href={basics.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm text-blue-600 hover:underline font-medium truncate"
                >
                  {basics.url}
                </a>
              </div>
            )}
          </div>
        </SectionCard>
      )}

      {/* Summary */}
      {basics?.summary && (
        <SectionCard title={t('cvInfo.professionalSummary')} icon={FileText}>
          <div className="bg-blue-50 rounded-lg p-4 border border-blue-100">
            <HtmlContent
              html={basics.summary}
              className="text-gray-800 leading-relaxed [&_ul]:list-disc [&_ul]:pl-5 [&_ul]:space-y-1 [&_p]:mb-2 last:[&_p]:mb-0"
            />
          </div>
        </SectionCard>
      )}

      {/* Work Experience */}
      {data.work && data.work.length > 0 && (
        <SectionCard title={t('cvInfo.workExperience')} icon={Briefcase}>
          <div className="relative">
            {data.work.map((work, idx) => (
              <WorkItem key={idx} work={work} />
            ))}
          </div>
        </SectionCard>
      )}

      {/* Education */}
      {data.education && data.education.length > 0 && (
        <SectionCard title={t('cvInfo.education')} icon={GraduationCap}>
          <div className="relative">
            {data.education.map((edu, idx) => (
              <EducationItem key={idx} edu={edu} />
            ))}
          </div>
        </SectionCard>
      )}

      {/* Skills */}
      {data.skills && data.skills.length > 0 && (
        <SectionCard title={t('cvInfo.skills')} icon={Wrench}>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {data.skills.map((skill, idx) => (
              <SkillItem key={idx} skill={skill} />
            ))}
          </div>
        </SectionCard>
      )}

      {/* Languages */}
      {data.languages && data.languages.length > 0 && (
        <SectionCard title={t('cvInfo.languages')} icon={Languages}>
          <div className="flex flex-wrap gap-2">
            {data.languages.map((lang, idx) => (
              <div
                key={idx}
                className="bg-gray-50 rounded-lg px-4 py-2.5 hover:bg-gray-100 transition-colors border border-gray-200"
              >
                <span className="font-semibold text-gray-900">{lang.language}</span>
                {lang.fluency && (
                  <span className="text-sm text-gray-600 ml-2">• {lang.fluency}</span>
                )}
              </div>
            ))}
          </div>
        </SectionCard>
      )}

      {/* Certificates */}
      {data.certificates && data.certificates.length > 0 && (
        <SectionCard title={t('cvInfo.certificates')} icon={Award}>
          <div className="space-y-2">
            {data.certificates.map((cert, idx) => (
              <div
                key={idx}
                className="bg-gray-50 rounded-lg p-4 hover:bg-gray-100 transition-colors"
              >
                <h4 className="font-semibold text-gray-900 mb-1">{cert.name}</h4>
                <div className="flex items-center justify-between gap-3">
                  {cert.issuer && (
                    <p className="text-sm text-gray-700 font-medium">{cert.issuer}</p>
                  )}
                  {cert.date && (
                    <p className="text-xs text-gray-500 whitespace-nowrap">{cert.date}</p>
                  )}
                </div>
              </div>
            ))}
          </div>
        </SectionCard>
      )}

      {/* Awards */}
      {data.awards && data.awards.length > 0 && (
        <SectionCard title={t('cvInfo.awards')} icon={Award}>
          <div className="space-y-2">
            {data.awards.map((award, idx) => (
              <div
                key={idx}
                className="bg-gray-50 rounded-lg p-4 hover:bg-gray-100 transition-colors"
              >
                <h4 className="font-semibold text-gray-900 mb-1">{award.title}</h4>
                <div className="flex items-center justify-between gap-3">
                  {award.awarder && (
                    <p className="text-sm text-gray-700 font-medium">{award.awarder}</p>
                  )}
                  {award.date && (
                    <p className="text-xs text-gray-500 whitespace-nowrap">{award.date}</p>
                  )}
                </div>
                {award.summary && (
                  <p className="text-sm text-gray-600 mt-2 leading-relaxed">{award.summary}</p>
                )}
              </div>
            ))}
          </div>
        </SectionCard>
      )}
    </div>
  );
}
